// src/components/GroupsPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { db, functions } from '../firebase';
import {
    collection, query, where, getDocs, orderBy,
    doc, addDoc, deleteDoc, writeBatch, Timestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

// Import Font Awesome Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faUserPlus, faTrashAlt, faSpinner, faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

// Import react-bootstrap components
import { Button, Card, Row, Col, Alert, Badge, Spinner, Container, Modal, Form } from 'react-bootstrap';

// Import the Auth Context hook
import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';

// --- Callable Function References ---
const deleteMemberCallable = httpsCallable(functions, 'deleteMember');
const deleteFamilyCallable = httpsCallable(functions, 'deleteFamily');

// --- Main Component: GroupsPage ---
function GroupsPage() {
    const { currentUser, isAdmin, userOrgId, loadingAuth } = useAuth();
    const navigate = useNavigate();

    // Local state
    const [groups, setGroups] = useState([]);
    const [groupMap, setGroupMap] = useState(new Map());
    const [membersMap, setMembersMap] = useState(new Map());
    const [allMembers, setAllMembers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [rolesByGroup, setRolesByGroup] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal States
    const [showAddRole, setShowAddRole] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);

    // State for Add Role Modal Form
    const [selectedMemberId, setSelectedMemberId] = useState('');
    const [roleLabel, setRoleLabel] = useState('');
    const [isSubmittingRole, setIsSubmittingRole] = useState(false);
    const [modalError, setModalError] = useState('');


    // --- Fetch core data (Groups, Members, Roles) ---
    const fetchData = useCallback(async () => {
        if (!userOrgId) {
            if (!loadingAuth && currentUser) {
                 setError("Organization details missing from your account. Cannot load groups.");
                 console.error("GroupsPage: OrgId missing from context after auth loaded.");
            }
            setLoading(false);
            return;
        }
        console.log("GroupsPage: Fetching data for orgId:", userOrgId);
        setLoading(true); setError(null); let isMounted = true;
        try {
            const groupsQuery = query(collection(db, 'group'), where('orgId', '==', userOrgId), orderBy('groupName'));
            const membersQuery = query(collection(db, 'member'), where('orgId', '==', userOrgId), orderBy('surname'), orderBy('name'));
            const rolesQuery = query(collection(db, 'role'), where('orgId', '==', userOrgId));

            const [groupsSnap, membersSnap, rolesSnap] = await Promise.all([ getDocs(groupsQuery), getDocs(membersQuery), getDocs(rolesQuery) ]);

            if (!isMounted) return;

            const fetchedGroups = groupsSnap.docs.map(d => ({ ...d.data(), id: d.id, name: d.data().groupName }));
            const newGroupMap = new Map(fetchedGroups.map(g => [g.id, g]));

            const fetchedMembers = membersSnap.docs.map(d => ({
                ...d.data(),
                id: d.id,
                name: d.data().name || '',
                surname: d.data().surname || '',
                familyId: d.data().familyId || null,
                photo: d.data().photo,
                imageUpdatedAt: d.data().imageUpdatedAt,
            }));
            const newMembersMap = new Map(fetchedMembers.map(m => [m.id, m]));

            const fetchedRoles = rolesSnap.docs.map(d => ({ ...d.data(), id: d.id }));

            setGroups(fetchedGroups); setGroupMap(newGroupMap); setMembersMap(newMembersMap);
            setAllMembers(fetchedMembers); setRoles(fetchedRoles); setError(null);
        } catch (err) { console.error("GroupsPage: Error fetching data", err); if (isMounted) { setError(err.message || "Failed to load group data."); } }
        finally { if (isMounted) { setLoading(false); } }
    }, [userOrgId, loadingAuth, currentUser]);

    useEffect(() => {
        if (!loadingAuth && userOrgId) { fetchData(); }
        else if (!loadingAuth && !userOrgId && currentUser) {
            setError("Organization details missing from your account. Cannot load groups.");
            setLoading(false);
        } else if (!loadingAuth && !currentUser) {
            setGroups([]); setGroupMap(new Map()); setMembersMap(new Map());
            setAllMembers([]); setRoles([]); setRolesByGroup({});
            setLoading(false); setError(null);
        }
    }, [loadingAuth, userOrgId, fetchData, currentUser]);

    // Effect to process roles and include member photo details
    useEffect(() => {
        if (loading || !roles || groupMap.size === 0 || membersMap.size === 0 || !userOrgId) {
            if (!loading && groupMap.size > 0 && roles.length > 0 && membersMap.size === 0) {
                console.warn("Processing roles, but membersMap is empty or groups/roles exist without matching members.");
            }
            setRolesByGroup({});
            return;
        }
        const grouped = {}; groupMap.forEach((_, groupId) => { grouped[groupId] = []; });

        roles.forEach(role => {
            const matchedGroup = groupMap.get(role.groupId);
            const matchedMember = membersMap.get(role.memberId);

            if (matchedGroup && matchedMember && role.orgId === userOrgId && matchedGroup.orgId === userOrgId && matchedMember.orgId === userOrgId) {
                const nameToDisplay = role.memberName || `${matchedMember.name || ''} ${matchedMember.surname || ''}`.trim() || 'Unknown Member';
                const familyIdToUse = role.familyId || matchedMember.familyId || null;

                grouped[role.groupId].push({
                    roleId: role.id,
                    memberId: role.memberId,
                    groupId: role.groupId,
                    label: role.label || '',
                    name: nameToDisplay,
                    familyId: familyIdToUse,
                    photo: matchedMember.photo,
                    imageUpdatedAt: matchedMember.imageUpdatedAt,
                });
            } else { console.warn(`Data consistency issue for Role ${role.id} (memberId: ${role.memberId}, groupId: ${role.groupId}) or member/group not found in current maps.`); }
        });
        Object.keys(grouped).forEach(groupId => grouped[groupId].sort((a, b) => a.name.localeCompare(b.name)));
        setRolesByGroup(grouped);
    }, [groupMap, membersMap, roles, loading, userOrgId]);


    // --- CRUD Handlers ---
    const handleDeleteGroup = useCallback(async (groupId, groupName) => {
        if (!groupId || !userOrgId || !isAdmin) return;
        if (!window.confirm(`Delete group "${groupName}" and all its roles? This action cannot be undone.`)) return;
        setError(null);
        try {
            const rolesToDeleteQuery = query(collection(db, 'role'), where('orgId', '==', userOrgId), where('groupId', '==', groupId));
            const rolesToDeleteSnap = await getDocs(rolesToDeleteQuery);
            const batch = writeBatch(db);
            rolesToDeleteSnap.forEach(doc => batch.delete(doc.ref));
            batch.delete(doc(db, 'group', groupId));
            await batch.commit();
            setGroups(prev => prev.filter(g => g.id !== groupId));
            setRolesByGroup(prev => {
                const newRoles = {...prev};
                delete newRoles[groupId];
                return newRoles;
            });
        } catch (err) { console.error("Error deleting group:", err); setError("Failed to delete group: " + err.message); }
    }, [userOrgId, isAdmin]);

    const handleAddRole = useCallback(async (groupId, memberId, label, familyId, memberName) => {
        if (!groupId || !memberId || !userOrgId || !isAdmin) {
            console.error("handleAddRole prerequisites not met:", { groupId, memberId, userOrgId, isAdmin });
            return Promise.reject(new Error("Cannot add role due to missing data or permissions."));
        }
        console.log(`Attempting to add member ${memberId} (${memberName}, familyId: ${familyId}) to group ${groupId} with label "${label}"`);
        try {
            const groupData = groups.find(g => g.id === groupId);
            await addDoc(collection(db, 'role'), {
                groupId: groupId,
                memberId: memberId,
                familyId: familyId,
                memberName: memberName,
                label: label || "",
                orgId: userOrgId,
                groupName: groupData?.name || "",
                cDate: Timestamp.now()
            });
             return Promise.resolve();
        } catch (err) {
            console.error("Error adding role:", err);
            return Promise.reject(err);
        }
    }, [userOrgId, isAdmin, groups]);

     const handleDeleteRole = useCallback(async (roleId, memberName, groupName) => {
        if (!roleId || !userOrgId || !isAdmin) return;
        if (!window.confirm(`Remove ${memberName} from the group "${groupName}"?`)) return;
        setError(null);
        try {
            await deleteDoc(doc(db, 'role', roleId));
            setRoles(prevRoles => prevRoles.filter(r => r.id !== roleId));
        }
        catch (err) { console.error("Error deleting role:", err); setError("Failed to delete role: " + err.message); }
    }, [userOrgId, isAdmin]);


    // --- Modal Handlers ---
    const handleOpenAddRoleModal = (group) => { setSelectedGroup(group); setSelectedMemberId(''); setRoleLabel(''); setModalError(''); setShowAddRole(true); };
    const handleCloseAddRoleModal = () => { setShowAddRole(false); setSelectedGroup(null); setModalError(''); setSelectedMemberId(''); setRoleLabel('');};
    const handleModalSubmit = async () => {
        setModalError('');
        if (!selectedMemberId) { setModalError("Please select a member to add."); return; }
        if (!selectedGroup) { setModalError("Selected group information is missing (internal error)."); return; }
        setIsSubmittingRole(true);

        const selectedMember = allMembers.find(m => m.id === selectedMemberId);
        let familyIdToAdd = null;
        let memberNameToAdd = '';

        if (selectedMember) {
            familyIdToAdd = selectedMember.familyId || null;
            memberNameToAdd = `${selectedMember.name || ''} ${selectedMember.surname || ''}`.trim();
        } else {
            setModalError("Could not find selected member details. Please try again.");
            setIsSubmittingRole(false);
            return;
        }
        if (!memberNameToAdd) {
             console.warn(`Constructed memberName is empty for memberId ${selectedMemberId}. Using 'Unknown Member'.`);
             memberNameToAdd = 'Unknown Member';
        }

        try {
            await handleAddRole(selectedGroup.id, selectedMemberId, roleLabel, familyIdToAdd, memberNameToAdd);
            handleCloseAddRoleModal();
            fetchData();
        } catch (err) { setModalError(err.message || "Failed to add member to group."); }
        finally { setIsSubmittingRole(false); }
    };

    const availableMembersForDropdown = useMemo(() => {
        if (!selectedGroup || !rolesByGroup[selectedGroup.id] || !allMembers) { return allMembers || []; }
        const memberIdsInGroup = new Set(rolesByGroup[selectedGroup.id].map(role => role.memberId));
        return allMembers.filter(member => !memberIdsInGroup.has(member.id));
    }, [selectedGroup, rolesByGroup, allMembers]);


    // --- Render Logic ---
    if (loadingAuth) { return ( <Container className="text-center mt-5"><Spinner animation="border" /><p className="mt-2">Loading User...</p></Container> ); }
    if (!currentUser) { return ( <Container className="mt-3"><Alert variant="warning">Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view groups.</Alert></Container> ); }
    if (error && !loading) {
        return (
            <Container className="mt-3">
                <Row className="mx-1 mb-3 align-items-center">
                    <Col> <nav aria-label="breadcrumb"> <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small mb-0"> <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li> <li className="breadcrumb-item active" aria-current="page">Groups</li> </ol> </nav> </Col>
                    {isAdmin && ( <Col xs="auto"> <Link to="/groups/add" className="btn btn-success"> <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Group </Link> </Col> )}
                </Row>
                <Alert variant="danger" onClose={() => setError(null)} dismissible>Error: {error}</Alert>
            </Container>
        );
    }
    if (loading) { return ( <Container className="text-center mt-5"><Spinner animation="border" /><p className="mt-2">Loading Groups Data...</p></Container> ); }

    return (
        <Container fluid className="mt-3">
            <Row className="mx-1 mb-3 align-items-center">
                <Col> <nav aria-label="breadcrumb"> <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small mb-0"> <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li> <li className="breadcrumb-item active" aria-current="page">Groups</li> </ol> </nav> </Col>
                {isAdmin && ( <Col xs="auto"> <Link to="/groups/add" className="btn btn-success"> <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Group </Link> </Col> )}
            </Row>

            {error && <Alert variant="danger" className="mx-1" onClose={() => setError(null)} dismissible>{error}</Alert>}

            {groups.length === 0 && !loading && (
                 <Card className="m-3">
                    <Card.Body className="text-center">
                        <h5>Groups</h5>
                        <p className="text-muted">
                            {isAdmin ? "No groups created yet. Use the 'Create Group' button to get started." : "No groups have been added for this organization yet."}
                        </p>
                    </Card.Body>
                </Card>
            )}

            {groups.map(group => (
                <Card key={group.id} className="mb-3 mx-1 shadow-sm">
                    <Card.Header as="h3" className="d-flex justify-content-between align-items-center bg-light py-2">
                        <span>{group.name}</span>
                        {isAdmin && (
                            <div>
                                <Button variant="outline-primary" size="sm" className="me-2 py-1 px-2" title="Add Member to Group" onClick={() => handleOpenAddRoleModal(group)} >
                                    <FontAwesomeIcon icon={faUserPlus} /> <span className="d-none d-sm-inline">Add Member</span>
                                </Button>
                                <Button variant="outline-danger" size="sm" className="py-1 px-2" title="Delete Group" onClick={() => handleDeleteGroup(group.id, group.name)}>
                                    <FontAwesomeIcon icon={faTrashAlt} /> <span className="d-none d-sm-inline">Delete Group</span>
                                </Button>
                            </div>
                        )}
                    </Card.Header>
                    <Card.Body>
                        <Row className="g-2">
                            {(!rolesByGroup[group.id] || rolesByGroup[group.id].length === 0) ? (
                                <Col xs={12}><p className="text-muted fst-italic">No members in this group.</p></Col>
                            ) : (
                                rolesByGroup[group.id] && rolesByGroup[group.id].map(role => {
                                    const photoCacheKey = role.imageUpdatedAt instanceof Timestamp
                                                          ? role.imageUpdatedAt.toMillis().toString()
                                                          : (role.imageUpdatedAt || role.memberId);

                                    return (
                                        <Col xs={6} sm={4} md={3} lg={2} key={role.roleId} className="mb-2">
                                            {/* UPDATED Link component */}
                                            <Link
                                                to={role.familyId ? `/families/${role.familyId}/member/${role.memberId}` : (isAdmin ? `/members/edit/${role.memberId}` : '#')}
                                                className={`text-decoration-none text-dark ${!isAdmin && !role.familyId ? 'disabled-link' : ''}`}
                                                onClick={(e) => { if (!isAdmin && !role.familyId) e.preventDefault();}}
                                                title={role.familyId ? `View ${role.name}'s Details` : (isAdmin ? `Edit ${role.name}` : role.name)}
                                            >
                                                <Card className="text-center h-100 shadow-sm position-relative role-card">
                                                    <Card.Body className="d-flex flex-column align-items-center p-2">
                                                        <MemberPhotoAvatar
                                                            src={role.photo}
                                                            alt={role.name || 'Member'}
                                                            size={60}
                                                            cacheKey={photoCacheKey}
                                                        />
                                                        <h6 className="mb-1 fw-bold mt-1" style={{fontSize: '0.85rem'}}>{role.name}</h6>
                                                        {role.label && ( <Badge bg="secondary" pill className="w-auto mb-2 px-2 py-1" style={{whiteSpace: 'normal', fontSize: '0.7rem'}}>{role.label}</Badge> )}
                                                        {isAdmin && ( <Button variant="outline-danger" size="sm" className="position-absolute top-0 end-0 m-1 p-0 lh-1" style={{ width: '20px', height: '20px', fontSize: '0.7rem', borderRadius: '50%' }} title={`Remove ${role.name} from group`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeleteRole(role.roleId, role.name, group.name); }}> <FontAwesomeIcon icon={faTrash} /> </Button> )}
                                                    </Card.Body>
                                                </Card>
                                            </Link>
                                        </Col>
                                    );
                                })
                            )}
                        </Row>
                    </Card.Body>
                </Card>
            ))}

            {selectedGroup && (
                 <Modal show={showAddRole} onHide={handleCloseAddRoleModal} centered>
                    <Modal.Header closeButton> <Modal.Title>Add Member to "{selectedGroup.name}"</Modal.Title> </Modal.Header>
                    <Modal.Body>
                        {modalError && <Alert variant="danger" size="sm">{modalError}</Alert>}
                        <Form onSubmit={(e) => { e.preventDefault(); handleModalSubmit();}}>
                            <Form.Group className="mb-3" controlId="selectMember">
                                <Form.Label>Select Member <span className="text-danger">*</span></Form.Label>
                                <Form.Select aria-label="Select member to add" value={selectedMemberId} onChange={(e) => { setSelectedMemberId(e.target.value); setModalError(''); }} required disabled={isSubmittingRole} >
                                    <option value="" disabled>-- Select a Member --</option>
                                    {availableMembersForDropdown.length > 0 ? (
                                        availableMembersForDropdown
                                            .sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`))
                                            .map(member => ( <option key={member.id} value={member.id}> {member.name} {member.surname} </option> ))
                                    ) : ( <option value="" disabled>No available members found</option> )}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="roleLabelInput">
                                <Form.Label>Role Label (Optional)</Form.Label>
                                <Form.Control type="text" placeholder="E.g., Chairperson, Tenor, Volunteer" value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} disabled={isSubmittingRole} maxLength={50} />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseAddRoleModal} disabled={isSubmittingRole}> Cancel </Button>
                        <Button variant="primary" onClick={handleModalSubmit} disabled={isSubmittingRole || !selectedMemberId} >
                            {isSubmittingRole ? ( <> <FontAwesomeIcon icon={faSpinner} spin className="me-2" /> Adding... </> ) : ( "Add Member" )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Container>
    );
}

export default GroupsPage;
